import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';


const ProjectDetailContainer = styled.div`
  padding: 6rem 1.5rem 5rem;
  background: rgb(var(--bg));
  min-height: 100vh;
  max-width: 1180px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 5rem 1.25rem 4rem;
  }

  @media (max-width: 480px) {
    padding: 4.5rem 1rem 3rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2.75rem;
  padding: 2.25rem 1.5rem 1.9rem;
  border-radius: 22px;
  background: rgb(var(--fg) / 0.035);
  color: rgb(var(--fg));
  border: 1px solid rgb(var(--fg) / 0.06);
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 20% 20%, rgba(197, 204, 212, 0.12), transparent 45%),
      radial-gradient(circle at 82% 22%, rgba(197, 204, 212, 0.06), transparent 35%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 480px) {
    margin-bottom: 2rem;
    padding: 1.75rem 1rem 1.4rem;
    border-radius: 18px;
  }
`;

const HeaderTextBlock = styled.div`
  max-width: 900px;
  margin: 0 auto 1.5rem;
`;

const Title = styled.h1`
  font-size: 2.7rem;
  color: rgb(var(--fg) / 0.95);
  margin-bottom: 0.4rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 2.15rem;
  }

  @media (max-width: 480px) {
    font-size: 1.85rem;
  }
`;

const Date = styled.p`
  font-size: 0.9rem;
  color: rgb(var(--gray) / 0.7);
  letter-spacing: 0.02em;

  @media (max-width: 768px) {
    font-size: 0.82rem;
  }
`;

const TagsContainer = styled.div`
  margin-top: 0;
  display: flex;
  justify-content: center;
  gap: 0.65rem;
  flex-wrap: wrap;
  margin-bottom: 0;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  color: rgb(var(--fg) / 0.9);
  padding: 0.48rem 0.95rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  border: 1px solid rgba(197, 204, 212, 0.3);
  backdrop-filter: blur(4px);
  transition: transform 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: rgb(var(--fg) / 0.08);
    transform: translateY(-1px);
    box-shadow: 0 10px 20px rgb(0 0 0 / calc(0.3 * var(--shadow-mul)));
  }

  @media (max-width: 768px) {
    font-size: 0.72rem;
  }
`;

const Image = styled.img`
  display: block;
  max-width: 820px;
  max-height: 560px;
  width: auto;
  height: auto;
  object-fit: contain;
  margin: 0 auto 2.5rem auto;
  border-radius: 18px;
  border: 1px solid rgb(var(--fg) / 0.08);
  background: rgb(var(--fg) / 0.04);
  box-shadow: 0 18px 36px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  filter: brightness(0.92) saturate(0.95);
  transition: filter 0.35s;

  @media (max-width: 768px) {
    max-width: 92%;
    max-height: 420px;
  }

  @media (max-width: 480px) {
    max-width: 100%;
    max-height: 320px;
    border-radius: 14px;
  }
`;

const CoverImage = styled.img`
  display: block;
  width: 100%;
  max-width: 860px;
  height: 360px;
  object-fit: cover;
  object-position: center;
  margin: 1.5rem auto 1rem;
  border-radius: 18px;
  border: 1px solid rgb(var(--fg) / 0.08);
  box-shadow: 0 18px 36px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  background: rgb(var(--fg) / 0.04);
  filter: brightness(0.92) saturate(0.95);
  transition: filter 0.35s ease;

  &:hover {
    filter: brightness(1) saturate(1);
  }

  @media (max-width: 768px) {
    height: 260px;
  }

  @media (max-width: 480px) {
    height: 200px;
    max-width: 100%;
  }
`;

const Section = styled.div`
  margin-bottom: 1.75rem;
  padding: 1.6rem 1.45rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));

  @media (max-width: 480px) {
    margin-bottom: 1.25rem;
    padding: 1.2rem 1rem;
    border-radius: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.45rem;
  color: #c5ccd4;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgba(197, 204, 212, 0.25);
  padding-bottom: 0.75rem;
  font-weight: 800;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }

  @media (max-width: 480px) {
    font-size: 1.18rem;
    padding-bottom: 0.6rem;
  }
`;

const Text = styled.p`
  font-size: 1rem;
  line-height: 1.85;
  color: rgb(var(--fg) / 0.82);
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 0.96rem;
  }

  @media (max-width: 480px) {
    font-size: 0.92rem;
    line-height: 1.75;
  }
`;

const List = styled.ul`
  padding-left: 1.35rem;
  list-style-type: disc;
  color: rgb(var(--fg) / 0.82);
  margin: 0.5rem 0 0;

  @media (max-width: 480px) {
    padding-left: 1.1rem;
  }
`;

const ListItem = styled.li`
  margin-bottom: 0.72rem;
  line-height: 1.7;
  padding-left: 0.1rem;

  &::marker {
    color: #c5ccd4;
  }

  @media (max-width: 768px) {
    margin-bottom: 0.55rem;
  }
`;

const NotebookEmbed = styled.iframe`
  width: 100%;
  height: 800px;
  border: none;
  border-radius: 16px;
  box-shadow: 0 16px 36px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  margin: 1.5rem 0;
  background: rgb(var(--fg) / 0.88);
  border: 1px solid rgb(var(--fg) / 0.08);

  @media (max-width: 480px) {
    height: 400px;
    margin: 1.2rem 0;
    border-radius: 14px;
  }
`;

const CTABox = styled.div`
  margin: 1.25rem 0 1.5rem;
  padding: 1.15rem 1.2rem;
  border-radius: 18px;
  background: rgb(var(--fg) / 0.04);
  border: 1px solid rgba(197, 204, 212, 0.22);
  box-shadow: 0 12px 28px rgb(0 0 0 / calc(0.35 * var(--shadow-mul)));

  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 16px;
  }
`;
const CTATitle = styled.h3`
  margin: 0 0 0.45rem 0;
  font-size: 1.02rem;
  font-weight: 800;
  color: #c5ccd4;
`;
const CTAText = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.75;
  color: rgb(var(--fg) / 0.82);

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;
const CTALink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.9rem;
  padding: 0.78rem 1.25rem;
  border-radius: 999px;
  background: rgb(var(--fg) / 0.06);
  color: rgb(var(--fg) / 0.92);
  border: 1px solid rgb(var(--fg) / 0.14);
  text-decoration: none;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(197, 204, 212, 0.5);
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2rem auto 0;
  padding: 0.9rem 2rem;
  min-width: 220px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.92);
  background: rgb(var(--fg) / 0.06);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;
  text-align: center;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(197, 204, 212, 0.5);
  }

  @media (max-width: 768px) {
    padding: 0.85rem 1.5rem;
    font-size: 0.78rem;
  }

  @media (max-width: 480px) {
    width: 100%;
    min-width: 0;
    padding: 0.8rem 1.2rem;
    font-size: 0.75rem;
  }
`;

const SpotifyPlaylistCreatorProject = () => {
  const tags = ['Python', 'APIs', 'Automation', 'NLP'];
  const datePublished = '2024-08-03';
  const navigate = useNavigate();

  return (
    <ProjectDetailContainer>
      <Header>
        <HeaderTextBlock>
          <Title>Spotify Sentence-to-Playlist Automation</Title>
          <Date>Published on: {datePublished}</Date>
        </HeaderTextBlock>

        <CoverImage src="/images/psycho.jpg" alt="Spotify Playlist Creator" />

        <TagsContainer>
          {tags.map((tag, index) => (
            <Tag key={index}>{tag}</Tag>
          ))}
        </TagsContainer>
      </Header>
      
      <Section >
        <SectionTitle style={{ marginTop: '2rem' }}>Overview</SectionTitle>
        <Text>
          This project started as a playful experiment with the Spotify Web API — turning everyday sentences into playlists composed of matching song titles. The idea was light‑hearted, but the implementation required real structure: cleaning unstructured text, applying n‑gram search logic, resolving ambiguity, and orchestrating Spotify API calls reliably. The result is a small but fully functioning automation pipeline that transforms messy input into a coherent output.
        </Text>
      </Section>

      <Section>
        <SectionTitle>Technical Breakdown</SectionTitle>
        <List>
          <ListItem><strong>API Foundation</strong>: First hands‑on experience working with the Spotify Web API (Spotipy), including authentication flows, token refresh, and handling API rate limits.</ListItem>
          <ListItem><strong>Robust Request Handling</strong>: Implemented retry logic, exponential backoff, token renewal, and paginated queries supporting up to 400 returned tracks.</ListItem>
          <ListItem><strong>N‑gram Matching Engine</strong>: Built a sliding‑window search heuristic that tests multiple multi‑word combinations to maximize exact title matches.</ListItem>
          <ListItem><strong>Ambiguity & Fallback Logic</strong>: Designed graceful degradation by inserting placeholder tracks only when all matching strategies fail.</ListItem>
          <ListItem><strong>End‑to‑End Automation</strong>: A full pipeline from user input → text cleaning → search strategy selection → ranking best configuration → playlist creation with ordered output.</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Why It Matters</SectionTitle>
        <Text>
          This project was my introduction to API‑driven development. It taught me to think in terms of pipelines, uncertainty handling, and designing around external‑system constraints. Even though the concept is playful, it strengthened the same analytical and systems‑thinking habits that appear in my more advanced projects.
        </Text>
      </Section>


      <Section>
        <SectionTitle>Dependencies</SectionTitle>
        <List>
          <ListItem>Python 3.x (Developed with Python 3.7.6)</ListItem>
          <ListItem>Spotipy library for Spotify API interaction</ListItem>
          <ListItem>dotenv for environment variable management</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Workflow</SectionTitle>
        <Text>
          The script cleans the sentence, evaluates several n‑gram search strategies, selects the configuration with the best match rate, and automatically assembles the final playlist on the user’s Spotify account.
        </Text>
      </Section>

      <Section>
        <SectionTitle>Notebook</SectionTitle>
        <CTABox>
          <CTATitle>Explore the project code</CTATitle>
          <CTAText>
            This project lives inside a broader repository of API-based experiments and automations. Open the GitHub directory to view the notebook, scripts, and related project files.
          </CTAText>
          <CTALink
            href="https://github.com/kyriakos-papadopoulos/Projects/tree/main/API_Projects/Spotify/Text_2_Playlist"
            target="_blank"
            rel="noopener noreferrer"
          >
            View GitHub Directory
          </CTALink>
        </CTABox>
        <NotebookEmbed 
          src="/embeds/psycho_playlists.html"
          title="Jupyter Notebook"
        />
      </Section>

      <Section>
        <SectionTitle>Results</SectionTitle>
        <Image src="/images/psycho_demo.jpeg" alt="Spotify Playlist Result" />
        <Text style={{ fontStyle: 'italic', textAlign: 'center', color: '#666', marginTop: '-1rem' }}>
          Inspired by the internet trope where someone gets blocked everywhere and still finds a… creative way to send a message. This playlist is that energy.
        </Text>
      </Section>

      <Section>
        <SectionTitle>Known Issues</SectionTitle>
        <List>
            <ListItem>
            <strong>Common Words</strong>: Words like "and," "the," or "I" are often too generic, resulting in irrelevant or no matches. These words can significantly reduce the accuracy of the playlist generation.
            </ListItem>
            <ListItem>
            <strong>Unusual Words</strong>: Small or unusual words, especially those that don't commonly appear in song titles, may not have matches in Spotify’s catalog, leading to more placeholder tracks being added.
            </ListItem>
            <ListItem>
            <strong>API Limitations</strong>: The Spotify API may return incomplete results, especially for less popular tracks. This can affect the ability to find matches for more obscure song titles.
            </ListItem>
        </List>
        </Section>

      <Section>
        <SectionTitle>Possible Improvements</SectionTitle>
        <Text>
            Several enhancements could be made to improve the overall functionality and accuracy of the playlist generation:
        </Text>
        <List>
            <ListItem>
            <strong>Fuzzy Matching</strong>: Implementing fuzzy matching could help find songs with titles that are close but not exact matches to the words in the sentence. This would allow for more flexibility in the matching process and reduce the number of unmatched words.
            </ListItem>
            <ListItem>
            <strong>Thesaurus Integration</strong>: Using a thesaurus or synonym-based dictionary could expand the search options for words that have no exact matches. For example, if a word like "happy" doesn't yield results, the program could try similar words like "joyful" or "glad."
            </ListItem>
            <ListItem>
            <strong>Improved Placeholder Tracks</strong>: Instead of using generic placeholder tracks, there could be genre-based or mood-based placeholder suggestions that align with the overall theme of the sentence.
            </ListItem>
            <ListItem>
            <strong>User-Defined Preferences</strong>: Allow users to input preferences like favorite genres, artists, or moods, so that these preferences could guide track selection in ambiguous cases.
            </ListItem>
        </List>
        <Text><strong>Note:</strong> Advanced matching techniques may increase API calls and risk rate‑limiting depending on implementation.</Text>
        </Section>

      <Button onClick={() => navigate('/projects')}>Back to Projects</Button>
    </ProjectDetailContainer>
  );
};

// Export metadata for use in the Projects page
export const SpotifyPlaylistCreatorMetadata = {
    title: 'Spotify Sentence-to-Playlist Automation',
    description: 'A playful Python project that transforms sentences into Spotify playlists using API calls, n-gram matching, and automated end-to-end playlist generation.',
    tags: ['Python', 'APIs', 'Automation', 'NLP'],
    languages: ['Python', 'GitHub'],
    datePublished: '2024-08-03',
    path: '/projects/psycho-playlists',
    coverImage: '/images/psycho.jpg'
  };

export default SpotifyPlaylistCreatorProject;